import { useState, useEffect, useCallback } from "react";
import { ClientOverviewRepositoryImpl } from "../../data/repositories/ClientOverviewRepositoryImpl";
import { GetCurrentBalanceInsightsUseCase } from "../../domain/use-cases/GetCurrentBalanceInsightsUseCase";
import type { CurrentBalanceInsightItem } from "../../domain/entities/CurrentBalanceInsightsEntity";

const repository = new ClientOverviewRepositoryImpl();
const useCase = new GetCurrentBalanceInsightsUseCase(repository);

export interface CurrentBalanceInsightsData {
  insights: CurrentBalanceInsightItem[];
  noInsights: boolean;
  monthLabel: string;
  message: string | null;
  loading: boolean;
  error: string | null;
}

export interface UseCurrentBalanceInsightsOptions {
  /** When false, the insights API is not called. Set to true only after current balance has loaded. */
  enabled?: boolean;
}

export function useCurrentBalanceInsights(
  options?: UseCurrentBalanceInsightsOptions,
): CurrentBalanceInsightsData {
  const enabled = options?.enabled ?? true;

  const [insights, setInsights] = useState<CurrentBalanceInsightItem[]>([]);
  const [noInsights, setNoInsights] = useState(false);
  const [monthLabel, setMonthLabel] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInsights = useCallback(async () => {
    if (!enabled) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const entity = await useCase.execute();
      setInsights(entity.insights);
      setNoInsights(entity.noInsights);
      setMonthLabel(entity.monthLabel);
      setMessage(entity.message);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load current balance insights",
      );
      setInsights([]);
      setNoInsights(true);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchInsights();
  }, [enabled, fetchInsights]);

  return { insights, noInsights, monthLabel, message, loading, error };
}
